import { useMap } from "react-leaflet";
import { useEffect, useRef, useState, useMemo, useCallback, memo, useDeferredValue } from "react";
import { useInView } from 'react-intersection-observer';
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Divider,
    Typography,
    CircularProgress,
    Collapse,
    Chip,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Language
} from '@mui/icons-material';
import { searchAddresses, searchAddressesYandex, searchByText } from "../../http/calls";
import { getSVGAddressIcon, getSVGProviderIcon } from "../layers/helper";
import { addPulseMarker, aimToObject, removeDuplicatesByKey } from "../controls/helpers";
import { setAddress, setLayers } from "../../store/reducers/helpersReducer";
import { ktpIcon, pillarIcon, projectStatusOreolIcon } from "../layers/icons";
import { pillarSvgIcon } from "../layers/LayerPillars";
import { transformerSvgIcon } from "../layers/LayerTransformers";
import { selectElectricityFiltered, selectPillarsWithAccountingsFiltered } from "../../store/selectors/supervisionSelector";

const statusColors = {
    'construction': '#ff2b2b',
    'reconstruction': '#2196F3',
    'replacement': '#18c218'
}

const LazyListItem = ({ children, placeholderHeight = 30 }) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        rootMargin: '200px 0px', // Начинаем загрузку за 200px до того, как элемент появится в области видимости
    });

    return (
        <div ref={ref} style={{ minHeight: inView ? 'auto' : `${placeholderHeight}px` }}>
            {inView ? children : null}
        </div>
    );
};

// Memoized components for better performance
const AddressResultItem = memo(({ address, onSelect, getSVGProviderIcon, getSVGAddressIcon }) => (
    <ListItemButton
        onClick={() => onSelect(address)}
        sx={{
            py: 0.2,
            px: 1,
            color: 'white',
            '&:hover': {
                bgcolor: 'rgb(55, 55, 55)'
            }
        }}
    >
        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getSVGProviderIcon(address.provider ? address.provider : null)}
                {getSVGAddressIcon(address.addresstype)}
            </Box>
        </ListItemIcon>
        <ListItemText
            primary={address.display_name.split('Республика Дагестан,')[1] || address.display_name.split(', Дагестан,')[0]}
            slotProps={{
                primary: {
                    variant: 'body2',
                    sx: { fontSize: '0.75rem', color: 'white' }
                }
            }}
        />
    </ListItemButton>
));

const PowerPlantResultItem = memo(({ powerPlant, onSelect }) => (
    <ListItemButton
        onClick={() => onSelect(powerPlant)}
        sx={{
            py: 0.2,
            px: 1,
            color: 'white',
            '&:hover': {
                bgcolor: 'rgb(55, 55, 55)'
            }
        }}
    >
        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <img
                style={{ width: '20px', height: '20px' }}
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAABn0lEQVQ4ja2Vv0vDQBTHszk5CVIluYA66Z/h4j8gqKO7Uk0u6aKgqJ1FBwfp4CLE0txFBxcFfyzaLnbWsaCzLrrU9y6pJunlmkAPvvT1XfJ5ufdeXjTiBtdm5ao7DCFLQ8Nwgked8r24iBPcp32qfWQgSwDRocWW6fAdTbFk+8iQAmUXT2768yhVMCmw7+KFwxFCeZW4l10hyh/QlwuYhhlr59PE4c8i2ZS3wf6I7Kfxcn0qDcSc/gHxTzJaYxkAX+D/1iljALkzKNsVtsN/wP+pW2wpnibpkcdWT0fh5loUpGNQ/7gH+xf4YC962hqhfjUzhyWrboYw/qrbbL8fFkq3/QPYezMhrxPrHlFWGWAt6Kn3LFhPYUp4U1llUQybVTAyHi0TZvNmeGTfzQM8Em8PZTdSGD65w2/FfRveTK4+hHeyHRZFAhPHDTqgl9x9SCjbFn6bnUGrnKAA1hI2+KLCbeXuQ6N8MZeYJJDT9GQpWd7swD5MRKR8EdpjBYrTwN+4cC8OUxYlvopMnYHAoiMsAUwP2KLDNTFgh/0J+AX00TU9BokJrAAAAABJRU5ErkJggg=="
                alt="Power Plant"
            />
        </ListItemIcon>
        <ListItemText
            primary={powerPlant.name}
            slotProps={{
                primary: {
                    variant: 'body2',
                    sx: { fontSize: '0.75rem', color: 'white' }
                }
            }}
        />
    </ListItemButton>
));

const FeedingSubstationResultItem = memo(({ map, substation, onSelect }) => (
    <ListItemButton
        onClick={() => onSelect(substation)}
        sx={{
            py: 0.2,
            px: 1,
            color: 'white',
            '&:hover': {
                bgcolor: 'rgb(55, 55, 55)'
            }
        }}
    >
        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <img
                style={{ width: '20px', height: '20px' }}
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAABOklEQVQ4ja2VsW7CMBCGPTF26iMgJDsMcXjACpShEiysEAd2nqIIMdMOSLwBCyMz3BmcOM6dgQpLv+LElz+f706OyEq1Hyz7l3cIvcR9sgHlvvQiWYfPHqxv0MsZ5sIbgzIZisig1tGDNKSCe5PeJyr2MdKQC9ZGjVEx8pYhF4xkYHbQRp4cJflRyGlliDdsIJBlRu51IVc4p2IQhs1hm06e0pnK0wIqC6QhpdvZU4aZUVtLViSjm5qUfpoeGuq5LcQB6ZyhTxnmPGqIwbayDbqaEslbu4n1oausT1fJqDWVy2gfcnRA9kPlkjR0ZhxdbdbMZZVzrg8pOt+Mqjjbh5auVGcw+IXrzsqoYzX3ZdQfxmI38EX5Ep3utPvhlM7lt38fCtfxnaf68NUjLDRsHLD/OFzrA/bdv4Arfje8TgtGuk0AAAAASUVORK5CYII="
                alt="Feeding Substation"
            />
        </ListItemIcon>
        <ListItemText
            primary={substation.name}
            slotProps={{
                primary: {
                    variant: 'body2',
                    sx: { fontSize: '0.75rem', color: 'white' }
                }
            }}
        />
    </ListItemButton>
));

const TransformerResultItem = memo(({ transformer, onSelect, pillars, statusColors }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <ListItemButton
            onClick={() => onSelect(transformer)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 0.5,
                py: 0.2,
                px: 0.5,
                color: 'white',
                '&:hover': {
                    bgcolor: 'rgb(55, 55, 55)'
                }
            }}
        >
            {/* <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}> */}
            <Box sx={{ position: 'relative', width: 36, height: 36 }}>
                <Box style={{ position: 'absolute', margin: '8px 0 0 9px' }}>

                    {ktpIcon(transformer.accounting ? 'accounting' : 'default', 18, 18)}
                </Box>

                {['2', '3', '4', '7'].includes(transformer.layer) ?
                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                        {projectStatusOreolIcon('ktp', Number(transformer.layer), 36)}
                    </Box>
                    : null}
            </Box>
            {/* </ListItemIcon> */}

            <Typography
                variant="body2"
                sx={{ fontSize: '0.75rem', color: 'rgb(200, 200, 200)' }}

            >{`${`${transformer.name}${transformer.nickname ? '(' + transformer.nickname + ')' : ''}`}${transformer.town ? ' - ' + transformer.town : ''}`}</Typography>
        </ListItemButton>

        {/* Pillars for this transformer */}
        {pillars && pillars.filter(p => p.tp_id === transformer.fid.toString()).map((p, index) => (
            <ListItemButton
                key={`${p.fid}`}
                onClick={() => onSelect(p)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    gap: 1,
                    py: 0.3,
                    px: 1,
                    ml: 1.5,
                    color: 'rgb(200, 200, 200)',
                    '&:hover': {
                        bgcolor: 'rgb(45, 45, 45)'
                    }
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ fontSize: '0.75rem', color: 'rgb(200, 200, 200)' }}

                >{`${index + 1}.`}</Typography>
                {pillarIcon(p.type, p.status, p.material_type, p.supports_count, 14)}
                {/* <svg fill="#000000" width="14px" height="14px" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {p.tags.supports_count === 1 && (
                            <circle r="40" cx="50" cy="50" fill={p.tags.material_type === '1' ? 'none' : `var(--${p.status})`} stroke={`var(--${p.status})`} strokeWidth="20" />
                        )}
                        {p.tags.supports_count === 2 && (
                            <rect x="10" y="10" width="80" height="80" rx="5" fill={p.tags.material_type === '1' ? 'none' : `var(--${p.status})`} stroke={`var(--${p.status})`} strokeWidth="20" />
                        )}
                        {p.tags.supports_count === 3 && (
                            <polygon points="50,10 90,90 10,90" fill={p.tags.material_type === '1' ? 'none' : `var(--${p.status})`} stroke={`var(--${p.status})`} strokeWidth="20" />
                        )}
                    </svg> */}

                <Typography
                    variant="body2"
                    sx={{ fontSize: '0.75rem', color: 'rgb(200, 200, 200)' }}

                >{`${p.name}${p.accounting ? ' id' + p.accounting.id + `${p.accounting.checklist ? ' ' + p.accounting.checklist.join(',') : ''}` : ''}`}</Typography>
            </ListItemButton>
        ))}
    </Box>
));

// Компонент для отображения сводки результатов поиска
const SearchResultsSummary = memo(({ founded }) => {
    const getGroupName = (type) => {
        const names = {
            'addresses': 'Адреса',
            'addressesYandex': 'Адреса (Яндекс)',
            'powerPlants': 'Электростанции',
            'feedingSubstations': 'Питающие подстанции',
            'transformers': 'Трансформаторы',
            'pillars': 'Опоры'
        };
        return names[type] || type;
    };

    const getTotalCount = () => {
        return Object.values(founded).reduce((total, items) => {
            return total + (Array.isArray(items) ? items.length : 0);
        }, 0);
    };

    const totalCount = getTotalCount();
    
    if (totalCount === 0) return null;

    return (
        <Box sx={{ 
            p: 1, 
            bgcolor: 'rgb(25, 25, 25)', 
            borderTop: '1px solid rgb(60, 60, 60)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center'
        }}>
            <Typography variant="caption" sx={{ color: 'rgb(150, 150, 150)', mr: 1 }}>
                Найдено: {totalCount}
            </Typography>
            {Object.entries(founded).map(([type, items]) => {
                if (!Array.isArray(items) || items.length === 0) return null;
                return (
                    <Chip
                        key={type}
                        label={`${getGroupName(type)}: ${items.length}`}
                        size="small"
                        sx={{
                            height: '20px',
                            fontSize: '0.7rem',
                            bgcolor: 'rgb(45, 45, 45)',
                            color: 'rgb(200, 200, 200)',
                            '& .MuiChip-label': {
                                px: 0.5
                            }
                        }}
                    />
                );
            })}
        </Box>
    );
});

const ModalSearch = (props) => {
    const map = useMap();
    const dispatch = useDispatch()
    const [text, setText] = useState('')
    // const [debouncedText, setDebouncedText] = useState('')
    const [founded, setFounded] = useState({})
    const [isItemsFound, setisItemsFound] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const textRef = useRef()
    const modalRef = useRef(null);
    const abortControllerRef = useRef(null);
    const defferedSeachValue = useDeferredValue(text)
    const isLocalSearching = text !== defferedSeachValue;

    console.log(props)
    // Accept buttonPosition prop
    const { buttonPosition } = props || {};

    const electricity = useSelector(selectElectricityFiltered);

    const pillars = useSelector(selectPillarsWithAccountingsFiltered)

    // Debounce text input
    // useEffect(() => {
    //     const timer = setTimeout(() => {
    //         setDebouncedText(text);
    //     }, 500); // 300ms debounce

    //     return () => clearTimeout(timer);
    // }, [text]);

    // Memoized search words
    // const searchWords = useMemo(() => {
    //     return defferedSeachValue ? defferedSeachValue.toLowerCase().split(/\s+/).filter(word => word.length > 0) : [];
    // }, [defferedSeachValue]);

    // Memoized filtered results for electricity data
    const electricityResults = useCallback((seachValue) => {
        if (!seachValue.length) return {};
        console.log(seachValue)
        const searchWords = seachValue.toLowerCase().split(/\s+/).filter(word => word.length > 0) || []
        console.log(searchWords)
        const results = {};

        // Power Plants
        if (electricity.powerPlants?.length) {
            results.powerPlants = electricity.powerPlants.filter(host =>
                host.name && searchWords.every(word =>
                    host.name?.toLowerCase().includes(word)
                )
            );
        }

        // Feeding Substations
        if (electricity.feedingSubstations?.length) {
            results.feedingSubstations = electricity.feedingSubstations.filter(host =>
                host.name && searchWords.every(word =>
                    host.name?.toLowerCase().includes(word)
                )
            );
        }

        // Pillars and Transformers (optimized logic)
        if (pillars?.length) {
            const _pillars = pillars.filter(host =>
                searchWords.every(word =>
                    (host.name?.toLowerCase().includes(word)) ||
                    (host.accounting?.checklist?.join(' ').toLowerCase().includes(word)) ||
                    (host.accounting?.id?.toString().toLowerCase().includes(word))
                )
            );

            const transformersFromPillars = new Set(_pillars.map(pillar => pillar.tp_id));

            // Объединяем трансформаторы найденные напрямую и через опоры, удаляя дубликаты
            const directTransformers = electricity.transformers?.filter(host =>
                searchWords.every(word =>
                    (host.name?.toLowerCase().includes(word)) ||
                    (host.geodecode_display_name?.toLowerCase().includes(word)) ||
                    (host.nickname?.toLowerCase().includes(word))
                )
            ) || [];

            const transformersFromPillarsFiltered = electricity.transformers?.filter(host =>
                transformersFromPillars.has(host.fid.toString())
            ) || [];

            // Удаляем дубликаты, используя Set с fid
            const uniqueTransformers = new Map();
            [...directTransformers, ...transformersFromPillarsFiltered].forEach(transformer => {
                uniqueTransformers.set(transformer.fid, transformer);
            });
            const transformers = Array.from(uniqueTransformers.values());

            results.transformers = transformers;
            results.pillars = _pillars;
        }
        setFounded(results);
        console.log(results)
        // return results;
    }, [electricity, pillars]);


    useEffect(() => {
        electricityResults(defferedSeachValue)
    }, [defferedSeachValue])


    useEffect(() => {
        return () => {
            dispatch(setAddress(null))
            // Cancel any pending requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        }
    }, [])

    useEffect(() => {
        // console.log(founded)
        if (Object.keys(founded).find(key => founded[key].length > 0)) {
            setisItemsFound(false)
        } else {
            setisItemsFound(true)
        }
    }, [founded])

    // Updated useEffect to use debouncedText instead of text
    // useEffect(() => {
    //     if (debouncedText && debouncedText !== '') {
    //         search(debouncedText)
    //     } else {
    //         setFounded({})
    //     }
    // }, [debouncedText])

    // Combine electricity results with founded state
    useEffect(() => {
        setFounded(prev => ({
            ...prev,
            ...electricityResults
        }));
    }, [electricityResults]);

    function selectHost(host, layer = null) {
        if (layer) {
            dispatch(setLayers({ layer: layer, position: 1 }))
        }

        const zoom = map.getZoom()
        if (host.x_y_coord) {
            map.setView([
                host.x_y_coord.split(' ')[1],
                host.x_y_coord.split(' ')[0],
            ], zoom < 16 ? 16 : zoom, { duration: 1, animate: false })
        }
        if (host.coordinates) {
            const coord = host.coordinates[parseInt(host.coordinates.length / 2)]
            map.setView([
                coord[0].toString(),
                coord[1].toString()
            ], zoom < 16 ? 16 : zoom, { duration: 200, animate: false })
        }

        if (host.coords) {
            map.setView(host.coords, 18, { duration: 200, animate: false })
        }

        map.eachLayer(function (layer) {
            layer.closeTooltip()
        })
        setTimeout(() => {
            map.closePopup()
            map.eachLayer(function (layer) {
                if (layer.options.data === host.fid) {
                    layer.openTooltip()
                    addPulseMarker(map, layer)
                }
            })
        }, 500)
    }

    function selectAddress(address) {
        map.eachLayer(function (layer) {
            layer.closeTooltip()
        })
        map.closePopup()

        let minx = 100;
        let maxx = 0;
        let miny = 100;
        let maxy = 0;
        if (address.geojson.type === 'Point') {
            const x = address.geojson.coordinates[1]
            const y = address.geojson.coordinates[0]
            minx = x
            maxx = x
            miny = y
            maxy = y
        } else if (address.geojson.type === 'LineString') {
            address.geojson.coordinates.map(t => {
                const x = t[1]
                const y = t[0]
                minx = x < minx ? x : minx
                maxx = x > maxx ? x : maxx
                miny = y < miny ? y : miny
                maxy = y > maxy ? y : maxy
            })
        } else if (address.geojson.type === 'MultiPolygon') {
            address.geojson.coordinates.map(p => {
                p[0].map(c => {
                    const x = c[1]
                    const y = c[0]
                    minx = x < minx ? x : minx
                    maxx = x > maxx ? x : maxx
                    miny = y < miny ? y : miny
                    maxy = y > maxy ? y : maxy
                })
            })
        } else {
            address.geojson.coordinates[0].map(t => {
                const x = t[1]
                const y = t[0]
                minx = x < minx ? x : minx
                maxx = x > maxx ? x : maxx
                miny = y < miny ? y : miny
                maxy = y > maxy ? y : maxy
            })
        }

        map.flyToBounds([[minx, miny], [maxx, maxy]], { maxZoom: 16, animate: false })
        dispatch(setAddress(address))
    }

    const remoteSearch = useCallback(() => {
        if (defferedSeachValue && defferedSeachValue !== '') {
            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            setIsLoading(2)

            Promise.allSettled([
                searchAddressesYandex(text, { signal }),
                searchByText(text, { signal })
            ]).then(([yandexResult, textResult]) => {
                if (signal.aborted) return;

                const updates = {};

                if (yandexResult.status === 'fulfilled' && yandexResult.value?.length) {
                    updates.addressesYandex = yandexResult.value.map(r => ({
                        provider: 'Yandex',
                        osm_id: r.GeoObject.metaDataProperty.GeocoderMetaData.text,
                        addresstype: r.GeoObject.metaDataProperty.GeocoderMetaData.kind,
                        display_name: r.GeoObject.metaDataProperty.GeocoderMetaData.text,
                        geojson: {
                            type: 'Point',
                            coordinates: r.GeoObject.Point.pos.split(' ')
                        }
                    }));
                }

                if (textResult.status === 'fulfilled' && textResult.value) {
                    // Объединяем удаленные результаты с локальными, удаляя дубликаты
                    const remoteTransformers = textResult.value;
                    const existingTransformers = founded.transformers || [];

                    const combinedTransformersMap = new Map();
                    // Сначала добавляем существующие
                    existingTransformers.forEach(transformer => {
                        combinedTransformersMap.set(transformer.fid, transformer);
                    });
                    // Затем добавляем удаленные (перезаписывая, если такой fid уже есть)
                    remoteTransformers.forEach(transformer => {
                        combinedTransformersMap.set(transformer.fid, transformer);
                    });

                    updates.transformers = Array.from(combinedTransformersMap.values());
                }

                setFounded(prev => ({ ...prev, ...updates }));
                setIsLoading(0);
            }).catch(err => {
                if (!signal.aborted) {
                    setIsLoading(0);
                }
            });
        }
    }, [defferedSeachValue]);

    function search(text) {
        dispatch(setAddress(null))

        if (text && text !== '') {
            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            searchAddresses(text, { signal }).then(res => {
                if (signal.aborted) return;

                if (res && res.length) {
                    setFounded(prev => ({
                        ...prev,
                        addresses: res
                    }));
                }
            }).catch(err => {
                if (!signal.aborted) {
                    console.error('Search error:', err);
                }
            });
        }
    }

    // Вычисляем стили позиционирования на основе позиции кнопки
    const dialogBoxStyles = useMemo(() => {
        console.log(buttonPosition)
        const baseStyles = {
            position: 'absolute',
            zIndex: 1202,
            backgroundColor: '#1a1a1a',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid #333',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            color: '#ffffff',
            opacity: 0.95,
            p: 1,
            transformOrigin: 'left top',
            transform: window.innerWidth < 768 ? 'scale(0.8)' : 'scale(1)',
        };

        const responsiveStyles = {
            ...baseStyles,
            width: 'auto',
            minWidth: '280px',
            maxWidth: '350px',
            height: 'auto',
            maxHeight: '80vh',
            borderRadius: 4
        };

        // Вычисляем позицию на основе позиции кнопки
        if (buttonPosition) {
            console.log(buttonPosition);
            const modalWidth = 300;
            const modalHeight = 600;

            // Позиционируем справа от кнопки с отступом
            let left = buttonPosition.right + 8;
            let top = buttonPosition.top;

            // Проверяем, не выходит ли модальное окно за правый край экрана
            // if (left + modalWidth > window.innerWidth) {
            //     // Позиционируем слева от кнопки
            //     left = buttonPosition.left - modalWidth - 10;
            // }

            // Проверяем, не выходит ли за левый край
            // if (left < 10) {
            //     left = 10;
            // }

            // Проверяем, не выходит ли за нижний край экрана
            // if (top + modalHeight > window.innerHeight) {
            //     top = window.innerHeight - modalHeight - 10;
            // }

            // Проверяем, не выходит ли за верхний край
            // if (top < 10) {
            //     top = 10;
            // }

            return {
                ...responsiveStyles,
                left: `${left}px`,
                top: `${top}px`,
                margin: 0
            };
        }

        // Дефолтное позиционирование (если buttonPosition не передан)
        return {
            ...responsiveStyles,
            margin: '10px 10px 5px 5px'
        };
    }, [buttonPosition]);

    return (
        createPortal(
            <Box style={dialogBoxStyles}>
                <Paper
                    elevation={8}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: '100%',
                        maxHeight: '300px',
                        margin: buttonPosition ? '0' : '10px',
                        bgcolor: 'rgb(30, 30, 30)',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid rgb(60, 60, 60)'
                    }}
                >
                    {/* Search Input */}
                    <Box sx={{ p: 0.3 }}>
                        <TextField
                            inputRef={textRef}
                            fullWidth
                            size="small"
                            placeholder="Поиск..."
                            autoFocus
                            onChange={e => setText(e.target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {text && (
                                                <Tooltip title={isLocalSearching ? "Поиск..." : "Очистить"}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            if (isLocalSearching) return;
                                                            setText('');
                                                            textRef.current.value = '';
                                                            setIsLoading(false)
                                                        }}
                                                        sx={{ color: 'rgb(170, 170, 170)' }}
                                                    >
                                                        {isLocalSearching ?
                                                            <CircularProgress size={20} sx={{ color: 'rgb(170, 170, 170)' }} /> :
                                                            <ClearIcon fontSize="small" />
                                                        }
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {text && (
                                                <Tooltip title="Поиск в Яндексе">
                                                    <IconButton
                                                        size="small"
                                                        onClick={remoteSearch}
                                                        disabled={isLoading || isLocalSearching}
                                                        sx={{ color: 'rgb(170, 170, 170)' }}
                                                    >
                                                        {isLoading ? (
                                                            <CircularProgress size={20} sx={{ color: 'rgb(170, 170, 170)' }} />
                                                        ) : (
                                                            <Language fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </InputAdornment>
                                    )
                                }
                            }}
                            sx={{
                                height: '30px',
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'rgb(45, 45, 45)',
                                    color: 'white',
                                    '& fieldset': {
                                        borderColor: 'rgb(80, 80, 80)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(120, 120, 120)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(144, 202, 249)',
                                    },
                                },
                                '& .MuiInputBase-root': {
                                    height: '100%',
                                },
                                '& .MuiInputBase-input': {
                                    padding: '0px 10px',
                                    color: 'white',
                                    '&::placeholder': {
                                        color: 'rgb(150, 150, 150)',
                                        opacity: 1,
                                    },
                                }
                            }}
                        />
                    </Box>
                    {/* Results */}
                    {!isItemsFound && (
                        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <Divider sx={{ borderColor: 'rgb(70, 70, 70)' }} />
                            <List sx={{ flex: 1, overflow: 'auto', py: 0, bgcolor: 'rgb(35, 35, 35)' }} dense>
                                {founded && Object.keys(founded).map(type => {
                                    switch (type) {
                                        case 'addressesYandex':
                                            return founded[type] && founded[type].length ? founded[type].map(t => (
                                                <LazyListItem key={`${t.osm_id}`} placeholderHeight={30}>
                                                    <AddressResultItem
                                                        address={t}
                                                        onSelect={selectAddress}
                                                        getSVGProviderIcon={getSVGProviderIcon}
                                                        getSVGAddressIcon={getSVGAddressIcon}
                                                    />
                                                </LazyListItem>
                                            )) : null;

                                        case 'addresses':
                                            return founded[type] && founded[type].length ? founded[type].map(t => (
                                                <LazyListItem key={`${t.osm_id}`} placeholderHeight={30}>
                                                    <AddressResultItem
                                                        address={t}
                                                        onSelect={selectAddress}
                                                        getSVGProviderIcon={getSVGProviderIcon}
                                                        getSVGAddressIcon={getSVGAddressIcon}
                                                    />
                                                </LazyListItem>
                                            )) : null;

                                        case 'powerPlants':
                                            return founded[type] && founded[type].length ? founded[type].map(t => (
                                                <LazyListItem key={`${t.fid}`} placeholderHeight={30}>
                                                    <PowerPlantResultItem
                                                        powerPlant={t}
                                                        onSelect={(host) => aimToObject(map, host)}
                                                    />
                                                </LazyListItem>
                                            )) : null;

                                        case 'feedingSubstations':
                                            return founded[type] && founded[type].length ? founded[type].map(t => (
                                                <LazyListItem key={`${t.fid}`} placeholderHeight={30}>
                                                    <FeedingSubstationResultItem
                                                        substation={t}
                                                        onSelect={(host) => aimToObject(map, host)}
                                                    />
                                                </LazyListItem>
                                            )) : null;

                                        case 'transformers':
                                            return founded[type] && founded[type].length ? founded[type].map(t => {
                                                const pillarsForTransformer = founded.pillars?.filter(p => p.tp_id === t.fid.toString()) || [];
                                                const placeholderHeight = 40 + (pillarsForTransformer.length * 24); // Approximation
                                                return (
                                                    <LazyListItem key={`${t.fid}`} placeholderHeight={placeholderHeight}>
                                                        <TransformerResultItem
                                                            transformer={t}
                                                            onSelect={(host) => aimToObject(map, host)}
                                                            pillars={founded.pillars}
                                                            statusColors={statusColors}
                                                        />
                                                    </LazyListItem>
                                                )
                                            }) : null;

                                        default:
                                            return null;
                                    }
                                })}
                            </List>
                            {/* Сноска с результатами поиска */}
                            <SearchResultsSummary founded={founded} />
                        </Box>
                    )}
                </Paper>
            </Box>, document.body)


    )


}

export default ModalSearch;
